"use client";

import { useState, useEffect, useCallback } from "react";
import { Track } from "@/lib/track/track";
import { Lane } from "@/lib/track/lane";
import { VolumeIcon, VolumeOffIcon, EyeIcon, EyeOffIcon } from "../../atoms/icons";

// ─── Shared primitives ───

function ToggleBtn({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`shrink-0 w-6 h-6 flex items-center justify-center rounded transition-all duration-150 cursor-pointer hover:bg-white/[0.06] ${
        active
          ? "text-text-secondary hover:text-text-primary"
          : "text-text-muted hover:text-text-secondary"
      }`}
      title={title}
    >
      {children}
    </button>
  );
}

function TrackColorBar({ color, disabled }: { color: string; disabled: boolean }) {
  return (
    <div
      className={`w-[3px] self-stretch rounded-full shrink-0 min-h-5 transition-opacity duration-150 ${
        disabled ? "opacity-30" : "opacity-100"
      }`}
      style={{ backgroundColor: color }}
    />
  );
}

function NoteCount({ count }: { count: number }) {
  return (
    <span className="text-[9px] tabular-nums text-text-muted shrink-0 min-w-[28px] text-right">
      {count > 0 ? count : ""}
    </span>
  );
}

const trackRowClasses =
  "flex items-center gap-1.5 px-2 py-1.5 rounded-md hover:bg-white/[0.03] transition-colors duration-100";

// ─── Lane row (for multi-lane tracks) ───

function laneLabel(track: Track, lane: Lane): string {
  const origin = track.getControler().getLaneOrigin?.(lane);

  if (origin?.kind === 'song-part') {
    if (origin.hand === 'left') {
      return 'Main gauche';
    }

    if (origin.hand === 'right') {
      return 'Main droite';
    }
  }

  return lane.getInstrument()?.name ?? `Lane ${lane.getChannel()}`;
}

function LaneRow({
  lane,
  label,
  isLast,
  trackMuted,
  trackHidden,
}: {
  lane: Lane;
  label: string;
  isLast: boolean;
  trackMuted: boolean;
  trackHidden: boolean;
}) {
  const [isMuted, setIsMuted] = useState(lane.isMuted());
  const [isHidden, setIsHidden] = useState(lane.isHidden());
  const [noteCount, setNoteCount] = useState(lane.getNotesCount());

  useEffect(() => {
    setIsMuted(lane.isMuted());
    setIsHidden(lane.isHidden());
    setNoteCount(lane.getNotesCount());

    const unsub = lane.on((e) => {
      if (e.type === 'mute') setIsMuted(true);
      else if (e.type === 'unmute') setIsMuted(false);
      else if (e.type === 'hide') setIsHidden(true);
      else if (e.type === 'show') setIsHidden(false);
      else if (e.type === 'noteOn' || e.type === 'noteOff') {
        setNoteCount(lane.getNotesCount());
      }
    });

    return unsub;
  }, [lane]);

  const enabled = !isMuted && !isHidden;
  const effectivelyDisabled = trackMuted || trackHidden || !enabled;

  const handleToggle = useCallback(() => {
    if (enabled) {
      lane.mute();
      lane.hide();
    } else {
      lane.unmute();
      lane.show();
    }
  }, [lane, enabled]);

  return (
    <div className="flex items-center gap-1.5 pl-3 pr-1.5 py-[3px] rounded-sm hover:bg-white/[0.03] transition-colors duration-150 group relative">
      {/* Tree connector */}
      <div className="relative w-3 shrink-0 self-stretch flex items-center">
        <div
          className={`absolute left-0 top-0 w-px bg-white/[0.08] ${
            isLast ? "bottom-1/2" : "bottom-0"
          }`}
        />
        <div className="absolute left-0 top-1/2 w-full h-px bg-white/[0.08]" />
      </div>

      {/* Label */}
      <span
        className={`text-[10px] leading-none truncate flex-1 min-w-0 transition-colors duration-150 ${
          effectivelyDisabled ? "text-white/25" : "text-text-secondary"
        }`}
      >
        {label}
      </span>

      <NoteCount count={noteCount} />

      <ToggleBtn
        active={enabled}
        onClick={handleToggle}
        title={enabled ? "Désactiver" : "Activer"}
      >
        {enabled ? <EyeIcon className="w-3.5 h-3.5" /> : <EyeOffIcon className="w-3.5 h-3.5" />}
      </ToggleBtn>
    </div>
  );
}

// ─── Single-lane track row ───

export function SingleLaneTrackRow({ track }: { track: Track }) {
  const [isMuted, setIsMuted] = useState(track.isMuted());
  const [isHidden, setIsHidden] = useState(track.isHidden());
  const [lanes, setLanes] = useState<Lane[]>(track.getLanes());

  useEffect(() => {
    setLanes(track.getLanes());
    setIsMuted(track.isMuted());
    setIsHidden(track.isHidden());

    const unsub = track.onChange(() => {
      setLanes(track.getLanes());
      setIsMuted(track.isMuted());
      setIsHidden(track.isHidden());
    });

    return unsub;
  }, [track]);

  const lane = lanes[0];
  if (!lane) return null;

  const fullyDisabled = isMuted && isHidden;
  const instrument = lane.getInstrument();
  const controler = track.getControler();
  const label = controler.label ?? `Track`;

  return (
    <div className={trackRowClasses}>
      <TrackColorBar color={controler.color ?? "#8b5cf6"} disabled={fullyDisabled} />

      <div className="flex flex-col min-w-0 flex-1">
        <span
          className={`text-[11px] font-semibold leading-tight truncate transition-colors duration-150 ${
            fullyDisabled ? "text-white/30" : "text-text-primary"
          }`}
        >
          {label}
        </span>
        {instrument?.name && (
          <span className="text-[9px] text-text-muted leading-none truncate">
            {instrument.name}
          </span>
        )}
      </div>

      <ToggleBtn
        active={!isMuted}
        onClick={() => isMuted ? track.unmute() : track.mute()}
        title={isMuted ? "Rétablir le son" : "Couper le son"}
      >
        {isMuted ? <VolumeOffIcon className="w-3.5 h-3.5" /> : <VolumeIcon className="w-3.5 h-3.5" />}
      </ToggleBtn>

      <ToggleBtn
        active={!isHidden}
        onClick={() => isHidden ? track.show() : track.hide()}
        title={isHidden ? "Afficher" : "Masquer"}
      >
        {isHidden ? <EyeOffIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
      </ToggleBtn>
    </div>
  );
}

// ─── Multi-lane track row ───

export function MultiLaneTrackRow({ track }: { track: Track }) {
  const [isMuted, setIsMuted] = useState(track.isMuted());
  const [isHidden, setIsHidden] = useState(track.isHidden());
  const [lanes, setLanes] = useState<Lane[]>(track.getLanes());

  useEffect(() => {
    setLanes(track.getLanes());
    setIsMuted(track.isMuted());
    setIsHidden(track.isHidden());

    const unsub = track.onChange(() => {
      setLanes(track.getLanes());
      setIsMuted(track.isMuted());
      setIsHidden(track.isHidden());
    });

    return unsub;
  }, [track]);

  const fullyDisabled = isMuted && isHidden;
  const controler = track.getControler();
  const label = controler.label ?? `Track`;

  return (
    <div>
      {/* Track header */}
      <div className={trackRowClasses}>
        <TrackColorBar color={controler.color ?? "#8b5cf6"} disabled={fullyDisabled} />

        <div className="flex flex-col min-w-0 flex-1">
          <span
            className={`text-[11px] font-semibold leading-tight truncate transition-colors duration-150 ${
              fullyDisabled ? "text-white/30" : "text-text-primary"
            }`}
          >
            {label}
          </span>
          <span className="text-[9px] text-text-muted leading-none">
            {lanes.length} voix
          </span>
        </div>

        <ToggleBtn
          active={!isMuted}
          onClick={() => isMuted ? track.unmute() : track.mute()}
          title={isMuted ? "Rétablir le son" : "Couper le son"}
        >
          {isMuted ? <VolumeOffIcon className="w-3.5 h-3.5" /> : <VolumeIcon className="w-3.5 h-3.5" />}
        </ToggleBtn>

        <ToggleBtn
          active={!isHidden}
          onClick={() => isHidden ? track.show() : track.hide()}
          title={isHidden ? "Afficher tout" : "Masquer tout"}
        >
          {isHidden ? <EyeOffIcon className="w-3.5 h-3.5" /> : <EyeIcon className="w-3.5 h-3.5" />}
        </ToggleBtn>
      </div>

      {/* Lane rows */}
      <div className="ml-2">
        {lanes.map((lane, i) => (
          <LaneRow
            key={lane.getChannel() + "-" + i}
            lane={lane}
            label={laneLabel(track, lane)}
            isLast={i === lanes.length - 1}
            trackMuted={isMuted}
            trackHidden={isHidden}
          />
        ))}
      </div>
    </div>
  );
}
